"use client";

import {
  type CapabilityReport,
  detectCapabilities,
  type MediaCapability,
  missingCapabilities,
  type ToolId,
} from "mediatools";
import { useEffect, useState } from "react";

export type ToolSupport =
  | { status: "checking" }
  | { status: "supported"; report: CapabilityReport }
  | { status: "unsupported"; report: CapabilityReport; missing: MediaCapability[] };

/**
 * Probe the browser once and report whether the given tool can run. Tools use
 * this to render the {@link UnsupportedBrowserNotice} instead of a broken UI.
 *
 * Detection runs client-side only (the WebCodecs probes touch browser globals),
 * so the initial render is always `checking`.
 */
export function useToolSupport(tool: ToolId): ToolSupport {
  const [support, setSupport] = useState<ToolSupport>({ status: "checking" });

  useEffect(() => {
    let cancelled = false;
    detectCapabilities().then((report) => {
      if (cancelled) return;
      const missing = missingCapabilities(tool, report);
      setSupport(
        missing.length === 0
          ? { status: "supported", report }
          : { status: "unsupported", report, missing }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [tool]);

  return support;
}
